import { errorMessage } from "../api/client";
import { useAppUsers } from "../api/users";
import type { AppUserRead } from "../api/types";
import SensitiveToggle, { useSensitiveMask } from "../components/SensitiveToggle";
import { maskEmail, maskName, maskUid } from "../lib/mask";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function UserRow({ user, masked }: { user: AppUserRead; masked: boolean }) {
  return (
    <tr>
      <td>{(masked ? maskEmail(user.email) : user.email) ?? "—"}</td>
      <td>{(masked ? maskName(user.display_name) : user.display_name) ?? "—"}</td>
      <td className="mono muted small">{masked ? maskUid(user.uid) : user.uid}</td>
      <td className="muted small">{formatDate(user.created_at)}</td>
    </tr>
  );
}

export default function UsersPage() {
  const { data, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useAppUsers();
  const users = data?.pages.flatMap((page) => page.users);
  const mask = useSensitiveMask();

  return (
    <div className="users-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-sub">
            Mobile app users, read from the customer backend (the source of truth), ordered by
            uid; deleted accounts are hidden.
          </p>
        </div>
      </div>

      <section className="card">
        <div className="card-head">
          <h3>All users</h3>
          <SensitiveToggle mask={mask} />
        </div>
        {isLoading && <p className="muted small">Loading…</p>}
        {error && <p className="error">Failed to load: {errorMessage(error)}</p>}
        {users && users.length === 0 && <p className="muted small">No users yet.</p>}
        {users && users.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Uid</th>
                  <th>Signed up</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <UserRow key={u.uid} user={u} masked={mask.masked} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {hasNextPage && (
          <button
            type="button"
            className="btn"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        )}
      </section>
    </div>
  );
}
